import {NgModule} from '@angular/core';
import {Routes} from '@angular/router';
import {PageNotFoundComponent} from '../lib/views/page-not-found/page-not-found.component';
import {LoginComponent} from '../lib/views/login/login.component';
import {AuthGuard} from '../core/guards/auth.guard';
import {unsavedChangesGuard} from '../core/guards/unsaved-changes.guard';
import {HomeComponent} from '../lib/views/apps/home/home.component';
import {AppPropManagerComponent} from '../lib/views/app-prop-manager/app-prop-manager.component';
import {AppTenantPortalComponent} from '../lib/views/app-tenant-portal/app-tenant-portal.component';
import {DashboardTenantPortalComponent} from '../lib/views/app-tenant-portal/dashboard-tenant-portal/dashboard-tenant-portal.component';
import {
  ManageUnitsPropManagerComponent,
} from '../lib/views/app-prop-manager/manage-prop-manager/manage-units-prop-manager/manage-units-prop-manager.component';
import {ManagePropManagerComponent} from '../lib/views/app-prop-manager/manage-prop-manager/manage-prop-manager.component';
import {
  ConfigureUnitPropManagerComponent,
} from '../lib/views/app-prop-manager/manage-prop-manager/manage-units-prop-manager/child-manage-configure/configure-unit-prop-manager.component';
import {
  UnitUsersPropManagerComponent,
} from '../lib/views/app-prop-manager/manage-prop-manager/manage-units-prop-manager/child-manage-users/unit-users-prop-manager.component';
import {
  ManageStaffPropManagerComponent,
} from '../lib/views/app-prop-manager/manage-prop-manager/manage-staff-prop-manager/manage-staff-prop-manager.component';
import {UserProfileComponent} from '../lib/views/user-profile/user-profile.component';
import {ControlsComponent} from '../lib/views/controls/controls.component';
import {ControlsHubsComponent} from '../lib/views/controls/hubs/controls-hubs.component';
import {ControlsDevicesComponent} from '../lib/views/controls/devices/controls-devices.component';
import {ControlsTilesComponent} from '../lib/views/controls/tiles/controls-tiles.component';
import {DevicesExistingComponent} from '../lib/views/controls/devices/existing/devices-existing.component';
import {DevicesNewComponent} from '../lib/views/controls/devices/new/devices-new.component';
import {HubsExistingComponent} from '../lib/views/controls/hubs/existing/hubs-existing.component';
import {HubsNewComponent} from '../lib/views/controls/hubs/new/hubs-new.component';
import {TilesExistingComponent} from '../lib/views/controls/tiles/existing/tiles-existing.component';
import {TilesNewComponent} from '../lib/views/controls/tiles/new/tiles-new.component';
import {AppMosaicComponent} from '../lib/views/app-mosaic/app-mosaic.component';
import {DashboardMosaicComponent} from '../lib/views/app-mosaic/dashboard-mosaic/dashboard-mosaic.component';
import {ConfigurationMosaicComponent} from '../lib/views/app-mosaic/configuration-mosaic/configuration-mosaic.component';
import {AppSettingsMosaicComponent} from '../lib/views/app-mosaic/configuration-mosaic/app-settings/app-settings-mosaic.component';
import {TileGroupsMosaicComponent} from '../lib/views/app-mosaic/configuration-mosaic/tile-groups/tile-groups-mosaic.component';
import {AlarmsComponent} from '../lib/views/alarms/alarms.component';
import {AlarmsDashboardComponent} from '../lib/views/alarms/dashboard/alarms-dashboard.component';
import {AlarmsHistoryComponent} from '../lib/views/alarms/history/alarms-history.component';
import {AlarmEventDetailsComponent} from '../lib/views/alarms/event-details/alarm-event-details.component';
import {SecurityComponent} from '../lib/views/security/security.component';
import {PasswordResetComponent} from '../lib/views/password-reset/password-reset.component';
import {MustBeLoggedOutGuardGuard} from '../core/guards/must-be-logged-out-guard.guard';
import {AppsComponent} from '../lib/views/apps/apps.component';
import {ManageAppsComponent} from '../lib/views/apps/manage/manage-apps.component';
import {AppsExistingComponent} from '../lib/views/apps/manage/existing/apps-existing.component';
import {AppsNewComponent} from '../lib/views/apps/manage/new/apps-new.component';
import {InvitationsComponent} from '../lib/views/invitations/invitations.component';
import {UserRegistrationComponent} from '../lib/views/user-registration/user-registration.component';
import {SharingComponent} from '../lib/views/sharing/sharing.component';
import {AdminToolsComponent} from '../lib/views/admin-tools/admin-tools.component';
import {AdminAuthGuard} from '../core/guards/admin-auth.guard';
import {TileGlossaryComponent} from '../lib/views/tile-glossary/tile-glossary.component'

export const withUnsavedChangesGuard = (routes: Routes): Routes => routes.map(route => {
  if (route.redirectTo !== undefined) {
    return route;
  }

  return {
    ...route,
    canDeactivate: [unsavedChangesGuard, ...(route.canDeactivate ?? [])],
    ...(route.children ? {children: withUnsavedChangesGuard(route.children)} : {}),
  };
});

export const appRoutes: Routes = withUnsavedChangesGuard([
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full',
  },
  {
    path: 'register',
    component: UserRegistrationComponent,
    canActivate: [MustBeLoggedOutGuardGuard],
  },
  {
    path: 'reset-password',
    component: PasswordResetComponent,
    canActivate: [MustBeLoggedOutGuardGuard],
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'invitations',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: InvitationsComponent,
  },
  {
    path: 'admin-tools',
    canActivate: [AdminAuthGuard],
    canActivateChild: [AdminAuthGuard],
    component: AdminToolsComponent,
  },
  {
    path: 'sharing',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: SharingComponent,
  },
  {
    path: 'apps',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: AppsComponent,
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        component: HomeComponent,
      },
      {
        path: 'manage',
        component: ManageAppsComponent,
        children: [
          {
            path: '',
            redirectTo: 'existing',
            pathMatch: 'full',
          },
          {
            path: 'existing',
            component: AppsExistingComponent,
          },
          {
            path: 'new',
            component: AppsNewComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'profile',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: UserProfileComponent,
  },
  {
    path: 'security',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: SecurityComponent,
  },
  {
    path: 'alarms',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: AlarmsComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        component: AlarmsDashboardComponent,
      },
      {
        path: 'history',
        component: AlarmsHistoryComponent,
      },
      {
        path: 'events/:eventUuid',
        component: AlarmEventDetailsComponent,
      },
    ],
  },
  {
    path: 'controls',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: ControlsComponent,
    children: [
      {
        path: '',
        redirectTo: 'hubs',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        redirectTo: 'hubs',
      },
      {
        path: 'hubs',
        component: ControlsHubsComponent,
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
          {
            path: '',
            redirectTo: 'existing',
            pathMatch: 'full',
          },
          {
            path: 'existing',
            component: HubsExistingComponent,
          },
          {
            path: 'new',
            component: HubsNewComponent,
          },
        ],
      },
      {
        path: 'devices',
        component: ControlsDevicesComponent,
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
          {
            path: '',
            redirectTo: 'existing',
            pathMatch: 'full',
          },
          {
            path: 'existing',
            component: DevicesExistingComponent,
          },
          {
            path: 'new',
            component: DevicesNewComponent,
          },
        ],
      },
      {
        path: 'tiles',
        component: ControlsTilesComponent,
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
          {
            path: '',
            redirectTo: 'existing',
            pathMatch: 'full',
          },
          {
            path: 'existing',
            component: TilesExistingComponent,
          },
          {
            path: 'new',
            component: TilesNewComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'prop-manager/:appId',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: AppPropManagerComponent,
    children: [
      {
        path: '',
        redirectTo: 'manage',
        pathMatch: 'full',
      },
      {
        path: 'manage',
        component: ManagePropManagerComponent,
        children: [
          {
            path: '',
            redirectTo: 'units',
            pathMatch: 'full',
          },
          {
            path: 'staff',
            component: ManageStaffPropManagerComponent,
          },
          {
            path: 'units',
            component: ManageUnitsPropManagerComponent,
          },
          {
            path: 'units/:tenantAppId/configuration',
            component: ConfigureUnitPropManagerComponent,
          },
          {
            path: 'units/:tenantAppId/users',
            component: UnitUsersPropManagerComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'tenant-portal/:appId',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: AppTenantPortalComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        component: DashboardTenantPortalComponent,
      },
    ],
  },
  {
    path: 'mosaic/:appId',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: AppMosaicComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        component: DashboardMosaicComponent,
      },
      {
        path: 'configuration',
        component: ConfigurationMosaicComponent,
        children: [
          {
            path: '',
            redirectTo: 'tile-groups',
            pathMatch: 'full',
          },
          {
            path: 'app',
            component: AppSettingsMosaicComponent,
          },
          {
            path: 'tile-groups',
            component: TileGroupsMosaicComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'tile-glossary',
    component: TileGlossaryComponent,
    canActivate: [AuthGuard],
  },
  {
    path: '**',
    component: PageNotFoundComponent,
  },
]);

@NgModule({
  imports: [],
  providers: [],
})
export class AppModule {
}


/// From imports:
// StoreDevtoolsModule.instrument({
//   maxAge: 5, // Retains last 25 states
//   logOnly: environment.production, // Restrict extension to log-only mode
// }),
